import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Download, Share2, ArrowUpRight, ArrowDownRight, RefreshCw } from 'lucide-react';

const SUPPORTED_CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "MXN", "BRL", "INR", "CNY", "NZD"];

const PnLReportsTab = ({
  pnlReport,
  selectedYear,
  businessName,
  months,
  formatCurrency,
  baseCurrency = "USD",
  onBaseCurrencyChange,
  onExportExpensesCSV,
  onExportIncomeCSV,
  onExportPnLCSV,
  onShareWithAccountant,
}) => {
  if (!pnlReport) return null;

  const currenciesUsed = pnlReport.currencies_used || [];
  const isMultiCurrency = currenciesUsed.length > 1;

  return (
    <div className="space-y-6">
      {/* Export & Share Actions Card */}
      <Card className="border-[#B4C9BE] bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">Export & Share</h3>
              <p className="text-sm text-gray-600">Download reports or share with your accountant</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={onExportExpensesCSV}>
                <Download className="w-4 h-4 mr-1" /> Expenses CSV
              </Button>
              <Button variant="outline" size="sm" onClick={onExportIncomeCSV}>
                <Download className="w-4 h-4 mr-1" /> Income CSV
              </Button>
              <Button variant="outline" size="sm" onClick={onExportPnLCSV}>
                <Download className="w-4 h-4 mr-1" /> P&L CSV
              </Button>
              <Button size="sm" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" onClick={onShareWithAccountant}>
                <Share2 className="w-4 h-4 mr-1" /> Share with Accountant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Profit & Loss Statement - {selectedYear}</CardTitle>
            <CardDescription>{businessName}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Base Currency Selector */}
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-gray-400" />
              <Select value={baseCurrency} onValueChange={onBaseCurrencyChange}>
                <SelectTrigger className="w-[100px] h-9" data-testid="base-currency-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={onExportPnLCSV}>
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Multi-currency info banner */}
          {isMultiCurrency && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800" data-testid="multi-currency-banner">
              <strong>Multi-currency report:</strong> Transactions in {currenciesUsed.join(', ')} have been converted to {baseCurrency} using historical monthly exchange rates.
            </div>
          )}

          <div className="space-y-6">
            {/* Income Breakdown */}
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5" /> Income
              </h3>
              {Object.entries(pnlReport.income_by_category || {}).length === 0 ? (
                <p className="text-gray-500 text-sm">No income recorded</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(pnlReport.income_by_category || {}).map(([cat, data]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">{cat}</span>
                      <span className="font-medium text-green-600">{formatCurrency(data.total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 font-semibold bg-green-50 px-3 rounded">
                    <span>Total Income</span>
                    <span className="text-green-700">{formatCurrency(pnlReport.summary?.total_income)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Expense Breakdown */}
            <div>
              <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5" /> Expenses
              </h3>
              {Object.entries(pnlReport.expense_by_category || {}).length === 0 ? (
                <p className="text-gray-500 text-sm">No expenses recorded</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(pnlReport.expense_by_category || {}).map(([cat, data]) => (
                    <div key={cat} className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-700">{cat}</span>
                      <span className="font-medium text-red-600">{formatCurrency(data.total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between py-2 font-semibold bg-red-50 px-3 rounded">
                    <span>Total Expenses</span>
                    <span className="text-red-700">{formatCurrency(pnlReport.summary?.total_expenses)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Net Profit */}
            <div className="border-t-2 border-gray-200 pt-4">
              <div className={`flex justify-between text-xl font-bold p-4 rounded-lg ${pnlReport.summary?.net_profit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                <span>Net Profit / (Loss)</span>
                <span className={pnlReport.summary?.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}>
                  {formatCurrency(pnlReport.summary?.net_profit)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Breakdown</CardTitle>
          {isMultiCurrency && (
            <CardDescription>All amounts shown in {baseCurrency}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full min-w-[400px]">
              <thead>
                <tr className="border-b">
                  <th className="pb-3 text-left font-medium text-gray-600">Month</th>
                  <th className="pb-3 text-right font-medium text-gray-600">Income</th>
                  <th className="pb-3 text-right font-medium text-gray-600">Expenses</th>
                  <th className="pb-3 text-right font-medium text-gray-600">Net</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pnlReport.monthly_breakdown || {}).map(([month, data]) => (
                  <tr key={month} className="border-b">
                    <td className="py-3">{months.find(m => m.value === parseInt(month))?.label || month}</td>
                    <td className="py-3 text-right text-green-600">{formatCurrency(data.income)}</td>
                    <td className="py-3 text-right text-red-600">{formatCurrency(data.expenses)}</td>
                    <td className={`py-3 text-right font-medium ${data.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(data.net)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PnLReportsTab;
